import { FlexibleActionCard } from '@application/flexible_component/flexible_identifier/flexible_identifier_flexible_action_card.component';
import { FlexibleButtonIconModel } from '@application/flexible_component/models/flexible_button_icon';
import { type ComponentEnvelope, createComponentRegistry, type JsonObject, type JsonValue } from '@care-companion/protocol';
import { ChannelKind, HorizontalAlignment, OpenUrlWithKind } from '@visiba/api-vcs-client-v2';

const receptionButtonRegistry = createComponentRegistry({
	type: 'com.visibacare.reception-button',
	schemaVersion: 1,
	kind: 'interaction',
	parse: parseReceptionButton,
});

type ReceptionButtonPayload = JsonObject & {
	id: number;
	order: number;
	horizontalAlignment: HorizontalAlignment | null;
	channel: ChannelKind | null;
	title: string;
	subtitle: string;
	url: string;
	requiresAuthentication: boolean;
	openAnotherApp: boolean;
	hasBrandedNavbar: boolean;
	icon: { type: 'svgUrl' | 'pngUrl'; origin: string } | null;
	openUrlWith: OpenUrlWithKind;
};

export function CareCompanionReceptionButton({ component }: { component: ComponentEnvelope }): JSX.Element | null {
	const decoded = receptionButtonRegistry.decode(component);
	if (!decoded.valid || !decoded.known) return null;

	const payload = decoded.component.payload;
	return (
		<FlexibleActionCard
			component={
				new FlexibleButtonIconModel({
					...payload,
					horizontalAlignment: payload.horizontalAlignment ?? HorizontalAlignment.Start,
				})
			}
		/>
	);
}

function parseReceptionButton(payload: JsonValue): ReceptionButtonPayload {
	if (payload == null || typeof payload !== 'object' || Array.isArray(payload)) throw new Error('Invalid reception button payload.');

	const value = payload as Record<string, JsonValue>;
	if (
		typeof value.id !== 'number' ||
		typeof value.order !== 'number' ||
		typeof value.title !== 'string' ||
		typeof value.subtitle !== 'string' ||
		typeof value.url !== 'string' ||
		typeof value.requiresAuthentication !== 'boolean' ||
		typeof value.openAnotherApp !== 'boolean' ||
		typeof value.hasBrandedNavbar !== 'boolean' ||
		!isEnumValue(OpenUrlWithKind, value.openUrlWith)
	) {
		throw new Error('Invalid reception button payload.');
	}

	let icon: ReceptionButtonPayload['icon'] = null;
	if (value.icon != null && typeof value.icon === 'object' && !Array.isArray(value.icon)) {
		const iconValue = value.icon as Record<string, JsonValue>;
		if ((iconValue.type !== 'svgUrl' && iconValue.type !== 'pngUrl') || typeof iconValue.origin !== 'string') {
			throw new Error('Invalid reception button icon.');
		}
		icon = { type: iconValue.type, origin: iconValue.origin };
	}

	return {
		id: value.id,
		order: value.order,
		horizontalAlignment: isEnumValue(HorizontalAlignment, value.horizontalAlignment) ? value.horizontalAlignment : null,
		channel: isEnumValue(ChannelKind, value.channel) ? value.channel : null,
		title: value.title,
		subtitle: value.subtitle,
		url: value.url,
		requiresAuthentication: value.requiresAuthentication,
		openAnotherApp: value.openAnotherApp,
		hasBrandedNavbar: value.hasBrandedNavbar,
		icon,
		openUrlWith: value.openUrlWith,
	};
}

function isEnumValue<T extends Record<string, string>>(enumType: T, value: JsonValue): value is T[keyof T] {
	return typeof value === 'string' && Object.values(enumType).includes(value);
}
